import { Divider, Tabs } from 'antd'
import axios from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { ServerScaleSpecInfo } from '@/api/type-server.ts'
import CodePreviewIcon from '@/assets/icons/code-preview.svg?react'
import RefreshIcon from '@/assets/icons/reset.svg?react'
import { Loading, Trans } from '@/components'
import { Metric } from '@/models/metric.ts'
interface ScalingSpecMetricProp {
  scalingSpecResId: string
}

type PreviewResult = {
  status: string
  signal: string
  data: string
}

export default function ScalingSpecMetric({ scalingSpecResId }: ScalingSpecMetricProp) {
  const { t } = useTranslation('scaling')
  const [loading, setLoading] = useState(false)
  const [scaleSpec, setScaleSpec] = useState<ServerScaleSpecInfo>()
  const [metricList, setMetricList] = useState<Metric[]>([])
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewResult, setPreviewResult] = useState<PreviewResult>({
    status: '-',
    signal: '🟢',
    data: '...',
  })

  const targetTypeMap: { [key: number]: string } = {
    1: t('loadMetric.baselineTypeRatio'),
    3: t('loadMetric.baselineTypeNumeric'),
  }

  const getScaleSpec = useCallback(
    async (specId: string) => {
      setLoading(true)
      try {
        const res = await API.getScaleSpec({
          spec_id: specId,
        })
        if (res.code === 0) {
          setScaleSpec(res.spec)
          setMetricList(res.spec.metrics)
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    },
    [scalingSpecResId],
  )

  const onPreviewClick = (url: string) => {
    setPreviewLoading(true)
    axios
      .get(url)
      .then((res) => {
        setPreviewResult({
          status: res.status.toString(),
          signal: res.status < 300 ? '🟢' : '🔴',
          data: JSON.stringify(res.data, null, 2),
        })
      })
      .catch((error) => {
        console.log(error.message)
        setPreviewResult({
          status: error.response?.status.toString() || 'Unknown',
          signal: '🔴',
          data: JSON.stringify(error.message, null, 2),
        })
      })
      .finally(() => {
        setPreviewLoading(false)
      })
  }

  useEffect(() => {
    getScaleSpec(scalingSpecResId)
  }, [getScaleSpec, scalingSpecResId])

  const items = useMemo(() => {
    return metricList.map((metric, index) => {
      return {
        key: index.toString(),
        label: metric.name,
        children: (
          <div className="py-4">
            <div className="w-full grid grid-cols-12 gap-4">
              <div className="col-span-6">
                <div className="t-xs text-secondary mb-1">
                  <Trans i18nKey="loadMetric.metricName" ns="scaling">
                    Metric Name
                  </Trans>
                </div>
                <div className="t-sb">{metric.name}</div>
              </div>
              <div className="col-span-6">
                <div className="t-xs text-secondary mb-1">
                  <Trans i18nKey="loadMetric.description" ns="scaling">
                    Description
                  </Trans>
                </div>
                <div className="t-sb">{metric.desc}</div>
              </div>
              <div className="col-span-6">
                <div className="t-xs text-secondary mb-1">
                  <Trans i18nKey="loadMetric.baselineType" ns="scaling">
                    Baseline Type
                  </Trans>
                </div>
                <div className="t-sb">{targetTypeMap[metric.target.type]}</div>
              </div>
              <div className="col-span-6">
                <div className="t-xs text-secondary mb-1">
                  <Trans i18nKey="loadMetric.baselineValue" ns="scaling">
                    Baseline Value
                  </Trans>
                </div>
                <div className="t-sb">{metric.target.value}</div>
              </div>
              <div className="col-span-6">
                <div className="t-xs text-secondary mb-1">
                  <Trans i18nKey="loadMetric.retrieveAddress" ns="scaling">
                    Retrieve Address
                  </Trans>
                </div>
                <div className="t-sb">{metric.url || '-'}</div>
              </div>
            </div>
            <div>
              <div className="t-xs text-secondary flex space-x-2 items-center overflow-hidden">
                <div className="text-nowrap">
                  <Trans i18nKey="loadMetric.metricPreview" ns="scaling">
                    Metric Preview
                  </Trans>
                </div>
                <Divider />
              </div>
              <div className="rounded-xl ">
                {previewLoading && (
                  <div className="w-full flex items-center justify-center p-8">
                    <Loading />
                  </div>
                )}
                {!previewLoading && (
                  <>
                    <div className="rounded-t-xl bg-[#3e4149] py-1 px-4 t-xs text-white flex items-center justify-between">
                      <div className="flex space-x-1">
                        <div>
                          <Trans i18nKey="loadMetric.responseResult" ns="scaling">
                            Response result
                          </Trans>
                        </div>
                        <div>{previewResult.signal}</div>
                        <div>{previewResult.status}</div>
                      </div>
                      <div
                        className="cursor-pointer pl-4"
                        onClick={() => {
                          onPreviewClick(metric.url)
                        }}
                      >
                        <RefreshIcon />
                      </div>
                    </div>
                    <div className="rounded-b-xl bg-black py-2 px-4 t-xs font-[400] text-secondary max-h-40 overflow-y-auto">
                      <pre>
                        <code>{previewResult.data}</code>
                      </pre>
                    </div>
                  </>
                )}
              </div>
              <Divider className="my-4" />
              <div
                className="text-highlight flex space-x-1 cursor-pointer"
                onClick={() => {
                  onPreviewClick(metric.url)
                }}
              >
                <CodePreviewIcon width={16} height={16} />
                <span className="t-x-sb">
                  <Trans i18nKey="loadMetric.previewBtn" ns="scaling">
                    Preview
                  </Trans>
                </span>
              </div>
            </div>
          </div>
        ),
      }
    })
  }, [previewLoading, metricList, previewResult])

  return (
    <>
      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}
      {!loading && scaleSpec && (
        <div className="bg-panel-gray-100 rounded-lg flex flex-wrap p-4 t-sm text-tertiary">
          <Tabs defaultActiveKey="0" items={items} className="w-full" />
        </div>
      )}
    </>
  )
}
